import { all, call, fork, put, takeEvery } from 'redux-saga/effects';

import { customHeaders, supersetUrl } from '../../../../../../../Containers/Config/index.js';
import SUPERSET_CANVAS_CONFIG from '../../../../../../../Components/SupersetCanvas/superset.canvas.config.js';
import { buildChartDataPayload, buildLiveData } from '../../../../../../../Components/SupersetCanvas/helpers/payload.js';
import { SUPERSET_CANVAS_CHART_DATA, SUPERSET_CANVAS_CHART_DATA_ERROR, SUPERSET_CANVAS_CHART_DATA_LOADING, SUPERSET_CANVAS_CHART_DATA_SUCCESS } from '../../../../../actionType.js';
import apiCall from '../../../../../apiCall.js';

const { ENDPOINTS } = SUPERSET_CANVAS_CONFIG;

const readError = response => response?.data?.message || response?.data?.errors?.[0]?.message || response?.message || 'Failed to load chart data';

// payload: { cardId, chartId, headers, queryContext?, baseUrl? }
//
// Two entry points:
//   • builder — only a chartId is known, so fetch the chart detail first
//     to obtain its saved query_context, then run it.
//   • viewer  — the export payload already carries query_context, so the
//     detail call is skipped and the same query is replayed directly.
function* supersetCanvasChartData({ payload }) {
  const { cardId, chartId, queryContext } = payload || {};

  try {
    yield put({ type: SUPERSET_CANVAS_CHART_DATA_LOADING, payload: { cardId } });

    const filteredHeaders = { ...customHeaders };
    delete filteredHeaders['X-Requested-By'];
    const headers = { ...filteredHeaders, ...payload?.headers };
    const baseUrl = (payload?.baseUrl || supersetUrl || '').replace(/\/$/, '');

    let detail = payload?.detail ?? null;
    let queryBody = queryContext ?? null;

    if (!queryBody) {
      if (!chartId && chartId !== 0) {
        yield put({ type: SUPERSET_CANVAS_CHART_DATA_ERROR, payload: { cardId, message: 'No chart id or query context to fetch' } });
        return;
      }

      const detailResponse = yield call(apiCall, {
        url: `${baseUrl}${ENDPOINTS.CHART_DETAIL(chartId)}`,
        method: 'GET',
        headers,
      });

      if (detailResponse?.status !== 200) {
        yield put({ type: SUPERSET_CANVAS_CHART_DATA_ERROR, payload: { cardId, message: readError(detailResponse) } });
        return;
      }

      detail = detailResponse.data?.result;
      queryBody = buildChartDataPayload(detail);

      if (!queryBody) {
        yield put({ type: SUPERSET_CANVAS_CHART_DATA_ERROR, payload: { cardId, message: 'This chart has no saved query context in Superset' } });
        return;
      }
    }

    const dataResponse = yield call(apiCall, {
      url: `${baseUrl}${ENDPOINTS.CHART_DATA}`,
      method: 'POST',
      headers,
      data: queryBody,
    });

    if (dataResponse?.status !== 200) {
      yield put({ type: SUPERSET_CANVAS_CHART_DATA_ERROR, payload: { cardId, message: readError(dataResponse) } });
      return;
    }

    yield put({
      type: SUPERSET_CANVAS_CHART_DATA_SUCCESS,
      payload: {
        cardId,
        data: buildLiveData(detail ?? payload?.chartMeta ?? {}, dataResponse.data, queryBody),
      },
    });
  } catch (error) {
    yield put({ type: SUPERSET_CANVAS_CHART_DATA_ERROR, payload: { cardId, message: error?.message || 'Failed to load chart data' } });
  }
}

export function* watchSupersetCanvasChartData() {
  yield takeEvery(SUPERSET_CANVAS_CHART_DATA, supersetCanvasChartData);
}

function* supersetCanvasChartDataSaga() {
  yield all([fork(watchSupersetCanvasChartData)]);
}

export default supersetCanvasChartDataSaga;
